import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { FilterProvider } from './context/FilterContext';
import { PersonaProvider } from './context/PersonaContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TenantProvider } from './context/TenantContext';
import { HierarchyProvider } from './context/HierarchyContext';
import { SupportContextProvider } from './context/SupportContext';
import { ProductModeProvider, useProductMode } from './context/ProductModeContext';
import { Toaster, toast } from 'sonner';
import Layout from './components/Layout';
import SupportDrawer from './components/SupportDrawer';
import SupportFAB from './components/SupportFAB';
import ScopingModeBanner from './components/ScopingModeBanner';
import ReauthModal from './components/ReauthModal';
import { OnboardingProvider } from './components/onboarding';  // V1 Readiness: Welcome Wizard
// TenantDebugIndicator removed for production
import { SyncNotificationProvider } from './components/notifications/SyncNotificationProvider';

// Pages
import LoginPage from './pages/LoginPage';
import ForceChangePasswordPage from './pages/ForceChangePasswordPage';
import PublicIntakePage from './pages/PublicIntakePage';
import ExternalIntakeQueue from './pages/ExternalIntakeQueue';
import Home from './pages/Home';
import PortfolioDashboard from './pages/PortfolioDashboard';
import ProgramDashboard from './pages/ProgramDashboard';
import ProjectDashboard from './pages/ProjectDashboard';
import ProjectWorkItemsPage from './pages/ProjectWorkItemsPage';
import UploadCenter from './pages/UploadCenter';
import EpicsPage from './pages/EpicsPage';
import FeaturesPage from './pages/FeaturesPage';
import StoriesPage from './pages/StoriesPage';
import TasksPage from './pages/TasksPage';
import IssuesPage from './pages/IssuesPage';
import DependenciesPage from './pages/DependenciesPage';
import DecisionsPage from './pages/DecisionsPage';
import ReleasesPage from './pages/ReleasesPage';

// Persona Dashboards
import ExecutiveDashboard from './pages/ExecutiveDashboard';
import ExecutiveDashboardV2 from './pages/ExecutiveDashboardV2';
import ExecutiveHub from './pages/ExecutiveHub';  // V1 Readiness: Unified Executive Entry Point
import PMODashboardV2 from './pages/PMODashboardV2';
import ProjectManagerDashboardV2 from './pages/ProjectManagerDashboardV2';

// Admin Pages
import AdminDashboard from './pages/AdminDashboard';
import UsersManagement from './pages/UsersManagement';
import RolesPermissions from './pages/RolesPermissions';
import BillingAdmin from './pages/BillingAdmin';
import ProjectInvoicePage from './pages/ProjectInvoicePage';
import ConfigurationPage from './pages/ConfigurationPage';
import BrandThemingPage from './pages/BrandThemingPage';
import AuditLogsPage from './pages/AuditLogsPage';
import IntegrationsPage from './pages/IntegrationsPage';
import HelpSupportPage from './pages/HelpSupportPage';
import NotificationsPage from './pages/NotificationsPage';
import DocumentationPage from './pages/DocumentationPage';
import EnhancedScopeWorkspacePage from './pages/EnhancedScopeWorkspacePage';
import EnhancedScopeBaselinesPage from './pages/EnhancedScopeBaselinesPage';
import AIScopingAssistantPage from './pages/AIScopingAssistantPage';
import PartnerPortfolioPage from './pages/PartnerPortfolioPage';
import ConsultingPage from './pages/ConsultingPage';
import RateCardPage from './pages/RateCardPage';
import ScopeOpsPage from './pages/ScopeOpsPage';
import ConsultingSuiteAdminPage from './pages/ConsultingSuiteAdminPage';
import AIPMDashboard from './pages/AIPMDashboard';
import AgenticPMLandingPage from './pages/AgenticPMLandingPage';
import AgenticPMDashboard from './pages/AgenticPMDashboard';
import TemplateLibraryPage from './pages/TemplateLibraryPage';
import ScopingWizardPage from './pages/ScopingWizardPage';
import WBSStructurePage from './pages/WBSStructurePage';
import BillingAdminDashboard from './pages/BillingAdminDashboard';

// Platform Admin Pages (Multi-Tenant Management)
import TenantDirectoryPage from './pages/platform/TenantDirectoryPage';
import CreateTenantPage from './pages/platform/CreateTenantPage';
import TenantModulesPage from './pages/platform/TenantModulesPage';
import TenantAuthPage from './pages/platform/TenantAuthPage';
import SystemUsersPage from './pages/platform/SystemUsersPage';
import WBSSchemaDesignerPage from './pages/platform/WBSSchemaDesignerPage';

// Resource Management Pages (P4)
import ResourceCapacityPage from './pages/ResourceCapacityPage';
import ResourceUtilisationPage from './pages/ResourceUtilisationPage';
import ResourceExplorerPage from './pages/ResourceExplorerPage';
import PortfolioCapacityPage from './pages/PortfolioCapacityPage';
import ResourceLandingPage from './pages/ResourceLandingPage';
import TenantSetupPage from './pages/TenantSetupPage';
import ScenarioPlanningPage from './pages/ScenarioPlanningPage';
import ScenarioCompare from './pages/ScenarioCompare';
import SecuritySettings from './pages/SecuritySettings';
import PerformanceDashboard from './pages/PerformanceDashboard';
import AzureDevOpsSettings from './pages/AzureDevOpsSettings';
import JiraSettings from './pages/JiraSettings';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import DemandJourneyAnalytics from './pages/DemandJourneyAnalytics';
import CycleTimeAgingPage from './pages/CycleTimeAgingPage';

// Subscription Settings (Sprint 24)
import SubscriptionSettings from './pages/SubscriptionSettings';

// Demand Management Pages (Sprint 7)
import DemandList from './pages/DemandList';
import DemandBoard from './pages/DemandBoard';
import DemandForm from './pages/DemandForm';
import DemandView from './pages/DemandView';

// Notification Preferences (Sprint 8)
import NotificationPreferencesPage from './pages/NotificationPreferencesPage';

// Email Templates (Sprint 11)
import EmailTemplatesPage from './pages/EmailTemplatesPage';

// Webhook Settings (Sprint 9)
import WebhookSettingsPage from './pages/WebhookSettingsPage';
import DemandTemplatesPage from './pages/DemandTemplatesPage';

// Webhook Dashboard (Sprint 11)
import WebhookDashboard from './pages/WebhookDashboard';

// Template Analytics (Sprint 11)
import TemplateAnalyticsPage from './pages/TemplateAnalyticsPage';

// Financial Module (Sprint 12)
import FinancialModulePage from './pages/FinancialModulePage';

// Risk Matrix (Sprint 12)
import RiskMatrixPage from './pages/RiskMatrixPage';

// Governance & Portfolio (Sprint 14)
import GovernanceWorkspace from './pages/GovernanceWorkspace';
import ReBaselineComparison from './pages/ReBaselineComparison';
import PortfolioHierarchy from './pages/PortfolioHierarchy';
import CapacityHeatmap from './pages/CapacityHeatmap';

// Governance Dashboard (Sprint 15)
import GovernanceDashboard from './pages/GovernanceDashboard';

// P1-003 Governance Automation Dashboard
import P1003GovernanceDashboard from './pages/P1003GovernanceDashboard';

// Investment Planning (Sprint 16)
import InvestmentPlanning from './pages/InvestmentPlanning';

// Scheduled Reports (Sprint 16)
import ScheduledReportsPage from './pages/ScheduledReportsPage';

// Governance Audit Trail (Sprint 17)
import GovernanceAuditTrail from './pages/GovernanceAuditTrail';

// Delivery Throughput (Sprint 18)
import DeliveryThroughput from './pages/DeliveryThroughput';

// Unified Excel Intake (Sprint 18)
import UnifiedExcelIntake from './pages/UnifiedExcelIntake';

// Notification Settings (Sprint 19)
import NotificationSettingsPage from './pages/NotificationSettingsPage';

// SLA Settings (Sprint 19)
import SLASettingsPage from './pages/SLASettingsPage';

// Sprint 20 Pages
import ComplianceDashboard from './pages/ComplianceDashboard';
import TrustCenterPage from './pages/TrustCenterPage';
import NotificationInboxPage from './pages/NotificationInboxPage';
import ScenarioVersioningPage from './pages/ScenarioVersioningPage';
import ScenarioOptimizerPage from './pages/ScenarioOptimizerPage';
import IntegrationStatusPage from './pages/IntegrationStatusPage';

// Sprint 21 Pages
import PortfolioRoadmapPage from './pages/PortfolioRoadmapPage';

// Sprint 23 Pages
import ExecutivePortfolioPage from './pages/ExecutivePortfolioPage';

// P1-001: Decision Ledger (Governance & Decision Intelligence)
import DecisionLedgerPage from './pages/DecisionLedgerPage';

// P1-002: Intent Versioning (Decision Integrity)
import IntentVersioningPage from './pages/IntentVersioningPage';

// P1-004: Benefits Realization (Outcome Intelligence Layer)
import BenefitsDashboard from './pages/BenefitsDashboard';

// P1-005: Resource Intelligence (Capacity Intelligence Layer)
import ResourceDashboard from './pages/ResourceDashboard';

// P2: Executive Intelligence Layer
import ExecutiveIntelligenceDashboard from './pages/ExecutiveIntelligenceDashboard';

// Phase SO-1: Strategic Objectives Foundation (L7 Experience)
import StrategyHubPage from './pages/StrategyHubPage';

// AI PM Redirect Component - Redirects project-level AI PM to PM Assistant with project filter
const AIPMRedirect = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Redirect to PM Assistant with project filter
    navigate(`/ai/pm-assistant?project=${projectId}`, { replace: true });
  }, [projectId, navigate]);
  
  return null;
};

// Protected Route Component with Product Mode Guard and Redirect Notice
const ProtectedRoute = ({ children, requiredRoles = [], guardedInScoping = false }) => {
  const { isAuthenticated, loading, hasRole, mustChangePassword } = useAuth();
  const { isScopingOnly, isRouteAllowed, getRedirectPath, loading: modeLoading } = useProductMode();
  const location = useLocation();
  const [redirecting, setRedirecting] = useState(false);
  const [redirectPath, setRedirectPath] = useState(null);

  // Handle delayed redirect with toast notification
  useEffect(() => {
    // Check if we need to redirect due to scoping mode
    if (!loading && !modeLoading && isAuthenticated && guardedInScoping && isScopingOnly && !isRouteAllowed(location.pathname)) {
      // Show toast and set up delayed redirect
      toast.info('You do not have permission to access this page.', {
        duration: 1500,
        id: 'route-guard-notice', // Prevent duplicate toasts
      });
      setRedirecting(true);
      setRedirectPath(getRedirectPath(location.pathname));
    }
    
    // Check if we need to redirect due to role requirements
    if (!loading && !modeLoading && isAuthenticated && requiredRoles.length > 0 && !hasRole(...requiredRoles)) {
      toast.info('You do not have permission to access this page.', {
        duration: 1500,
        id: 'route-guard-notice',
      });
      setRedirecting(true);
      setRedirectPath('/unauthorized');
    }
  }, [loading, modeLoading, isAuthenticated, guardedInScoping, isScopingOnly, location.pathname, requiredRoles]);

  // Perform the redirect after delay
  useEffect(() => {
    if (redirecting && redirectPath) {
      const timer = setTimeout(() => {
        // Navigate will happen through the redirect state
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [redirecting, redirectPath]);

  if (loading || modeLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--clr-surface-subtle)]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--clr-brand-primary)]"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Force password change redirect - must be before other checks
  if (mustChangePassword && location.pathname !== '/force-change-password') {
    return <Navigate to="/force-change-password" replace />;
  }

  // Show loading state during redirect delay
  if (redirecting && redirectPath) {
    return (
      <DelayedRedirect to={redirectPath} delay={1500} />
    );
  }

  return children;
};

// Component to handle delayed redirect with visual feedback
const DelayedRedirect = ({ to, delay = 1500 }) => {
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldRedirect(true);
    }, delay);
    
    return () => clearTimeout(timer);
  }, [delay]);

  if (shouldRedirect) {
    return <Navigate to={to} replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--clr-surface-subtle)]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[var(--clr-brand-primary)] mx-auto mb-4"></div>
        <p className="text-[var(--clr-text-secondary)] text-sm">Redirecting...</p>
      </div>
    </div>
  );
};

// Layout wrapper that includes the scoping mode banner
const LayoutWithBanner = ({ children }) => {
  const { showModeBanner, modeBannerText } = useProductMode();
  
  return (
    <>
      {showModeBanner && <ScopingModeBanner productName={modeBannerText} />}
      <Layout>
        {children}
      </Layout>
    </>
  );
};

// Routes inside AuthProvider
const AppRoutes = () => {
  const { isAuthenticated, getPersonaLanding, mustChangePassword } = useAuth();
  const { isScopingOnly } = useProductMode();

  // Role-based landing redirect component
  const RoleBasedLanding = () => {
    // In scoping mode, always land on scope workspace
    if (isScopingOnly) {
      return <Navigate to="/scope-workspace" replace />;
    }
    const landingPath = getPersonaLanding();
    return <Navigate to={landingPath} replace />;
  };

  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={isAuthenticated && !mustChangePassword ? <Navigate to={isScopingOnly ? "/scope-workspace" : getPersonaLanding()} replace /> : <LoginPage />} 
      />
      
      {/* Force Password Change - Protected but allows access even when mustChangePassword is true */}
      <Route 
        path="/force-change-password" 
        element={isAuthenticated ? <ForceChangePasswordPage /> : <Navigate to="/login" replace />} 
      />
      
      {/* Public Demand Intake - No auth required */}
      <Route path="/public/demand-intake" element={<PublicIntakePage />} />

      {/* External Intake Queue - PMO Review */}
      <Route path="/public/intake-queue" element={
        <ProtectedRoute requiredRoles={['governance', 'pmo_power', 'admin', 'sysadmin']}>
          <LayoutWithBanner>
            <ExternalIntakeQueue />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Root route - Redirect to role-based dashboard */}
      <Route path="/" element={
        <ProtectedRoute>
          <RoleBasedLanding />
        </ProtectedRoute>
      } />

      {/* Legacy Portfolio Overview - Redirect to Executive Dashboard */}
      <Route path="/portfolio-overview" element={
        <ProtectedRoute guardedInScoping>
          <Navigate to="/dashboard/executive" replace />
        </ProtectedRoute>
      } />

      <Route path="/upload-center" element={
        <ProtectedRoute requiredRoles={['sysadmin', 'pmo_power', 'program_admin', 'pm']} guardedInScoping>
          <Navigate to="/delivery/excel-intake" replace />
        </ProtectedRoute>
      } />

      {/* Sprint 18: Excel Intake moved to Delivery */}
      <Route path="/delivery/excel-intake" element={
        <ProtectedRoute requiredRoles={['sysadmin', 'pmo_power', 'admin']} guardedInScoping>
          <LayoutWithBanner>
            <UnifiedExcelIntake />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* V1 READINESS: Executive Hub - Unified Executive Entry Point */}
      <Route path="/executive" element={
        <ProtectedRoute requiredRoles={['executive', 'pmo_power', 'pmo_standard', 'admin', 'sysadmin', 'program_admin']}>
          <LayoutWithBanner>
            <ExecutiveHub />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Persona Dashboards - Legacy routes redirect to Executive Hub */}
      <Route path="/dashboard/executive" element={
        <Navigate to="/executive?tab=overview" replace />
      } />

      {/* Archive: Old Executive Dashboard */}
      <Route path="/dashboard/executive-legacy" element={
        <ProtectedRoute guardedInScoping>
          <LayoutWithBanner>
            <ExecutiveDashboard />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/dashboard/pmo" element={
        <ProtectedRoute guardedInScoping>
          <LayoutWithBanner>
            <PMODashboardV2 />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/dashboard/project-manager" element={
        <ProtectedRoute guardedInScoping>
          <LayoutWithBanner>
            <ProjectManagerDashboardV2 />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* V1 READINESS: Executive Portfolio - Redirect to Executive Hub */}
      <Route path="/executive/portfolio" element={
        <Navigate to="/executive?tab=portfolio" replace />
      } />

      {/* Sprint 23: Legacy Portfolio Redirects (301 equivalent in React) */}
      <Route path="/portfolio/insights" element={
        <Navigate to="/executive?tab=portfolio" replace />
      } />
      <Route path="/portfolio/programs" element={
        <Navigate to="/executive?tab=portfolio" replace />
      } />
      <Route path="/portfolio/scenarios" element={
        <ProtectedRoute>
          <Navigate to="/scenarios" replace />
        </ProtectedRoute>
      } />

      {/* Dashboard Pages (Legacy/Detail) - Guarded in Scoping Mode */}
      <Route path="/portfolio/:portfolioId" element={
        <ProtectedRoute guardedInScoping>
          <LayoutWithBanner>
            <PortfolioDashboard />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/program/:programId" element={
        <ProtectedRoute guardedInScoping>
          <LayoutWithBanner>
            <ProgramDashboard />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/project/:projectId" element={
        <ProtectedRoute guardedInScoping>
          <LayoutWithBanner>
            <ProjectDashboard />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* AI PM Dashboard - Redirect to PM Assistant with project filter */}
      <Route path="/project/:projectId/ai-pm" element={
        <ProtectedRoute guardedInScoping>
          <LayoutWithBanner>
            <AIPMRedirect />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Agentic PM - Redirect to Dashboard */}
      <Route path="/ai/agentic-pm" element={<Navigate to="/ai/pm-assistant" replace />} />

      {/* PM Assistant - Level 3 Operational Assistant (Primary AI Dashboard) */}
      <Route path="/ai/pm-assistant" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <AgenticPMDashboard />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Legacy route redirect */}
      <Route path="/ai/agentic-pm-dashboard" element={<Navigate to="/ai/pm-assistant" replace />} />

      {/* Project Work Items - Conditionally guarded (depends on generateWorkItems) */}
      <Route path="/project/:projectId/work-items" element={
        <ProtectedRoute guardedInScoping>
          <LayoutWithBanner>
            <ProjectWorkItemsPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/work-items" element={
        <ProtectedRoute guardedInScoping>
          <LayoutWithBanner>
            <ProjectWorkItemsPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Hierarchy Pages - Guarded in Scoping Mode */}
      <Route path="/epics" element={
        <ProtectedRoute guardedInScoping>
          <LayoutWithBanner>
            <EpicsPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/features" element={
        <ProtectedRoute guardedInScoping>
          <LayoutWithBanner>
            <FeaturesPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/stories" element={
        <ProtectedRoute guardedInScoping>
          <LayoutWithBanner>
          <StoriesPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/tasks" element={
        <ProtectedRoute guardedInScoping>
          <LayoutWithBanner>
            <TasksPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Sprint 18: Hierarchy Shortcuts - Pre-filtered Work Item Routes */}
      <Route path="/delivery/epics" element={
        <ProtectedRoute guardedInScoping>
          <Navigate to="/epics" replace />
        </ProtectedRoute>
      } />
      <Route path="/delivery/features" element={
        <ProtectedRoute guardedInScoping>
          <Navigate to="/features" replace />
        </ProtectedRoute>
      } />
      <Route path="/delivery/stories" element={
        <ProtectedRoute guardedInScoping>
          <Navigate to="/stories" replace />
        </ProtectedRoute>
      } />
      <Route path="/delivery/tasks" element={
        <ProtectedRoute guardedInScoping>
          <Navigate to="/tasks" replace />
        </ProtectedRoute>
      } />

      {/* Tracking Pages - Guarded in Scoping Mode */}
      <Route path="/issues" element={
        <ProtectedRoute guardedInScoping>
          <LayoutWithBanner>
            <IssuesPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/dependencies" element={
        <ProtectedRoute guardedInScoping>
          <LayoutWithBanner>
            <DependenciesPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/decisions" element={
        <ProtectedRoute guardedInScoping>
          <LayoutWithBanner>
            <DecisionsPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/releases" element={
        <ProtectedRoute guardedInScoping>
          <LayoutWithBanner>
            <ReleasesPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Admin Pages */}
      <Route path="/admin" element={
        <ProtectedRoute requiredRoles={['sysadmin', 'pmo_power', 'admin', 'program_admin']}>
          <LayoutWithBanner>
            <AdminDashboard />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/admin/users" element={
        <ProtectedRoute requiredRoles={['sysadmin', 'pmo_power', 'admin', 'program_admin']}>
          <LayoutWithBanner>
            <UsersManagement />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/admin/roles-permissions" element={
        <ProtectedRoute requiredRoles={['sysadmin', 'admin', 'program_admin']}>
          <LayoutWithBanner>
            <RolesPermissions />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/admin/billing" element={
        <ProtectedRoute requiredRoles={['sysadmin']} guardedInScoping>
          <LayoutWithBanner>
            <BillingAdmin />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/admin/billing-dashboard" element={
        <ProtectedRoute requiredRoles={['sysadmin']}>
          <LayoutWithBanner>
            <BillingAdminDashboard />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Project Invoice Routes - Global Platform Only */}
      <Route path="/billing/invoice/new" element={
        <ProtectedRoute requiredRoles={['sysadmin']}>
          <ProjectInvoicePage />
        </ProtectedRoute>
      } />
      <Route path="/billing/invoice/:invoiceId" element={
        <ProtectedRoute requiredRoles={['sysadmin']}>
          <ProjectInvoicePage />
        </ProtectedRoute>
      } />

      <Route path="/admin/tenant-setup" element={
        <ProtectedRoute requiredRoles={['sysadmin', 'pmo_power', 'admin', 'program_admin']}>
          <LayoutWithBanner>
            <TenantSetupPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/admin/configuration" element={
        <ProtectedRoute requiredRoles={['sysadmin', 'pmo_power', 'admin', 'program_admin']}>
          <LayoutWithBanner>
            <ConfigurationPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/admin/brand" element={
        <ProtectedRoute requiredRoles={['sysadmin', 'pmo_power', 'admin', 'program_admin']}>
          <LayoutWithBanner>
            <BrandThemingPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/admin/audit" element={
        <ProtectedRoute requiredRoles={['sysadmin', 'pmo_power', 'admin', 'program_admin']}>
          <LayoutWithBanner>
            <AuditLogsPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/admin/integrations" element={
        <ProtectedRoute requiredRoles={['sysadmin', 'admin', 'program_admin']}>
          <LayoutWithBanner>
            <IntegrationsPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/admin/consulting-suite" element={
        <ProtectedRoute requiredRoles={['sysadmin', 'consulting_admin']}>
          <LayoutWithBanner>
            <ConsultingSuiteAdminPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/admin/templates" element={
        <ProtectedRoute requiredRoles={['sysadmin', 'pmo_power']}>
          <LayoutWithBanner>
            <TemplateLibraryPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Sprint 19: Notification Settings */}
      <Route path="/settings/notifications" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <NotificationSettingsPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Sprint 19: SLA Settings */}
      <Route path="/settings/sla" element={
        <ProtectedRoute requiredRoles={['sysadmin', 'pmo_power', 'admin']}>
          <LayoutWithBanner>
            <SLASettingsPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* R3-004: Compliance Dashboard merged into Trust Center */}
      <Route path="/compliance/dashboard" element={<Navigate to="/trust/security-and-compliance" replace />} />

      {/* Sprint 20: Trust Center (includes Compliance) */}
      <Route path="/trust/security-and-compliance" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <TrustCenterPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* R3-004: Notification Inbox redirects to Daily Signal */}
      <Route path="/notifications/inbox" element={<Navigate to="/notifications" replace />} />

      {/* Sprint 20: Scenario Versioning */}
      <Route path="/scenarios/:scenarioId/versions" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <ScenarioVersioningPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Sprint 20: Scenario Optimizer */}
      <Route path="/scenarios/:scenarioId/optimizer" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <ScenarioOptimizerPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Sprint 20: Integration Status */}
      <Route path="/settings/integrations/status" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <IntegrationStatusPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* R2-002: Universal Roadmap - Single entry point with context filters */}
      <Route path="/roadmap" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <PortfolioRoadmapPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Legacy: Redirect old portfolio/roadmap to universal /roadmap */}
      <Route path="/portfolio/roadmap" element={<Navigate to="/roadmap" replace />} />

      <Route path="/admin/personas" element={
        <ProtectedRoute requiredRoles={['sysadmin', 'pmo_power']}>
          <LayoutWithBanner>
            <UsersManagement />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/admin/analytics" element={
        <ProtectedRoute requiredRoles={['sysadmin', 'pmo_power']}>
          <LayoutWithBanner>
            <AdminDashboard />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/admin/support" element={
        <ProtectedRoute requiredRoles={['sysadmin', 'pmo_power']}>
          <LayoutWithBanner>
            <HelpSupportPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Notifications - Daily Signal - Guarded in Scoping Mode */}
      <Route path="/notifications" element={
        <ProtectedRoute guardedInScoping>
          <LayoutWithBanner>
            <NotificationsPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/settings/notifications" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <NotificationPreferencesPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/settings/notifications/scheduled-reports" element={
        <ProtectedRoute requiredRoles={['admin', 'owner', 'sysadmin', 'pmo_power']}>
          <LayoutWithBanner>
            <ScheduledReportsPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/settings/notifications/templates" element={
        <ProtectedRoute requiredRoles={['admin', 'owner', 'sysadmin']}>
          <LayoutWithBanner>
            <EmailTemplatesPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/admin/notifications" element={
        <ProtectedRoute requiredRoles={['sysadmin', 'pmo_power']} guardedInScoping>
          <LayoutWithBanner>
            <NotificationsPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Webhook Settings */}
      <Route path="/settings/webhooks" element={
        <ProtectedRoute requiredRoles={['sysadmin', 'admin', 'pmo_power', 'program_admin']}>
          <LayoutWithBanner>
            <WebhookSettingsPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* R3-005: Webhook Dashboard redirects to Webhook Settings */}
      <Route path="/webhooks/dashboard" element={<Navigate to="/settings/webhooks?tab=dashboard" replace />} />

      {/* Template Analytics (Sprint 11) */}
      <Route path="/analytics/templates" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <TemplateAnalyticsPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Demand Templates */}
      <Route path="/admin/demand-templates" element={
        <ProtectedRoute requiredRoles={['sysadmin', 'admin', 'pmo_power']}>
          <LayoutWithBanner>
            <DemandTemplatesPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Security Settings */}
      <Route path="/settings/security" element={
        <ProtectedRoute requiredRoles={['sysadmin', 'pmo_power']} guardedInScoping>
          <LayoutWithBanner>
            <SecuritySettings />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Subscription Settings - Sprint 24 */}
      <Route path="/settings/subscription" element={
        <ProtectedRoute requiredRoles={['sysadmin', 'pmo_power', 'admin']} guardedInScoping>
          <LayoutWithBanner>
            <SubscriptionSettings />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Admin Performance Dashboard */}
      <Route path="/admin/performance" element={
        <ProtectedRoute requiredRoles={['sysadmin', 'pmo_power']}>
          <LayoutWithBanner>
            <PerformanceDashboard />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Azure DevOps Integration Settings */}
      <Route path="/settings/integrations/azure-devops" element={
        <ProtectedRoute requiredRoles={['sysadmin', 'pmo_power']}>
          <LayoutWithBanner>
            <AzureDevOpsSettings />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Jira Integration Settings */}
      <Route path="/settings/jira" element={
        <ProtectedRoute requiredRoles={['sysadmin', 'pmo_power']}>
          <LayoutWithBanner>
            <JiraSettings />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Analytics Dashboard - Sprint 5 Reporting */}
      <Route path="/analytics" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <AnalyticsDashboard />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Demand Journey Analytics - Sprint 10 */}
      <Route path="/analytics/demand-journey" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <DemandJourneyAnalytics />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Financial Module (Sprint 12) */}
      <Route path="/financial" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <FinancialModulePage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Risk Matrix (Sprint 12) */}
      <Route path="/analytics/risk-matrix" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <RiskMatrixPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Governance & Portfolio (Sprint 14) */}
      <Route path="/governance" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <GovernanceWorkspace />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      <Route path="/governance/baselines" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <ReBaselineComparison />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      
      {/* Governance Dashboard (Sprint 15) */}
      <Route path="/analytics/governance-dashboard" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <GovernanceDashboard />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      
      {/* P1-003 Governance Automation Dashboard */}
      <Route path="/governance/p1003-dashboard" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <P1003GovernanceDashboard />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      
      {/* Governance Audit Trail (Sprint 17) */}
      <Route path="/governance/audit-trail" element={
        <ProtectedRoute requiredRoles={['admin', 'sysadmin', 'pmo_power', 'governance']}>
          <LayoutWithBanner>
            <GovernanceAuditTrail />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      
      {/* P1-001: Decision Ledger (Governance & Decision Intelligence) */}
      <Route path="/governance/decision-ledger" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <DecisionLedgerPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      
      {/* P1-002: Intent Versioning (Decision Integrity) */}
      <Route path="/governance/intent-versioning" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <IntentVersioningPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      
      {/* P1-004: Benefits Realization (Outcome Intelligence Layer) */}
      <Route path="/governance/benefits" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <BenefitsDashboard />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      
      {/* Phase SO-1: Strategy Hub Experience (L7 Experience) */}
      <Route path="/strategy-hub" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <StrategyHubPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      
      {/* P1-005: Resource Intelligence (Capacity Intelligence Layer) */}
      <Route path="/intelligence/resources" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <ResourceDashboard />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      
      {/* V1 READINESS: Executive Intelligence - Redirect to Executive Hub Intelligence Tab */}
      <Route path="/intelligence/executive" element={
        <Navigate to="/executive?tab=intelligence" replace />
      } />
      
      {/* Delivery Throughput (Sprint 18) */}
      <Route path="/analytics/delivery-throughput" element={
        <ProtectedRoute requiredRoles={['pmo_power', 'admin', 'sysadmin', 'executive']}>
          <LayoutWithBanner>
            <DeliveryThroughput />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      
      {/* Cycle Time & Aging Analytics */}
      <Route path="/analytics/cycle-time" element={
        <ProtectedRoute requiredRoles={['pmo_power', 'admin', 'sysadmin', 'executive']}>
          <LayoutWithBanner>
            <CycleTimeAgingPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      
      <Route path="/portfolio/hierarchy" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <PortfolioHierarchy />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      <Route path="/portfolio/capacity" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <CapacityHeatmap />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      <Route path="/portfolio/investments" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <InvestmentPlanning />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Demand Management (Sprint 7) */}
      <Route path="/demands" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <DemandBoard />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      <Route path="/demands/list" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <DemandList />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      <Route path="/demands/board" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <DemandBoard />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      <Route path="/demands/new" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <DemandForm />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      <Route path="/demands/:id" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <DemandView />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      <Route path="/demands/:id/edit" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <DemandForm />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Scoping & Requirements - Available in both modes */}
      <Route path="/scope-workspace" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <EnhancedScopeWorkspacePage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/scope-baselines" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <EnhancedScopeBaselinesPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Legacy Scope Pages (fallback) */}
      <Route path="/scope/workspace" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <EnhancedScopeWorkspacePage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/scope/baselines" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <EnhancedScopeBaselinesPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Scoping Wizard */}
      <Route path="/scope/wizard" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <ScopingWizardPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* WBS Structure Management */}
      <Route path="/wbs-structure" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <WBSStructurePage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* ScopeOps Landing Page */}
      <Route path="/scopeops" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <ScopeOpsPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/ai-scoping-assistant" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <AIScopingAssistantPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Consulting Mode - Agent-led Proposal Builder */}
      <Route path="/consulting" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <ConsultingPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Rate Card Management - Consulting Mode */}
      <Route path="/consulting/rate-card" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <RateCardPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Partner Portfolio - Feature flagged */}
      <Route path="/partner/portfolio" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <PartnerPortfolioPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Documentation / Help Center - Available in both modes */}
      <Route path="/docs" element={
        <ProtectedRoute>
          <DocumentationPage />
        </ProtectedRoute>
      } />

      <Route path="/help" element={
        <ProtectedRoute>
          <DocumentationPage />
        </ProtectedRoute>
      } />

      {/* Resource Management Routes (P4) */}
      <Route path="/resources" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <ResourceLandingPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      
      <Route path="/resources/capacity" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <ResourceCapacityPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      <Route path="/resources/utilisation" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <ResourceUtilisationPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* R3-003: Resource Explorer redirects to Resource Management */}
      <Route path="/resources/explorer" element={<Navigate to="/resources?tab=explorer" replace />} />

      {/* R3-003: Portfolio Capacity redirects to Capacity Planning */}
      <Route path="/resources/portfolio" element={<Navigate to="/resources/capacity" replace />} />

      {/* P9: Scenario Planning */}
      <Route path="/scenarios" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <ScenarioPlanningPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      <Route path="/scenarios/:scenarioId" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <ScenarioPlanningPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      <Route path="/scenarios/compare" element={
        <ProtectedRoute>
          <LayoutWithBanner>
            <ScenarioCompare />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Platform Admin Routes (sysadmin only) */}
      <Route path="/platform/tenants" element={
        <ProtectedRoute requiredRoles={['sysadmin']}>
          <LayoutWithBanner>
            <TenantDirectoryPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      <Route path="/platform/tenants/create" element={
        <ProtectedRoute requiredRoles={['sysadmin']}>
          <LayoutWithBanner>
            <CreateTenantPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      <Route path="/platform/tenants/:tenantId/edit" element={
        <ProtectedRoute requiredRoles={['sysadmin']}>
          <LayoutWithBanner>
            <TenantDirectoryPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      <Route path="/platform/tenants/:tenantId/modules" element={
        <ProtectedRoute requiredRoles={['sysadmin']}>
          <LayoutWithBanner>
            <TenantModulesPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      <Route path="/platform/tenants/:tenantId/auth" element={
        <ProtectedRoute requiredRoles={['sysadmin']}>
          <LayoutWithBanner>
            <TenantAuthPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      <Route path="/platform/tenants/:tenantId/wbs-schema" element={
        <ProtectedRoute requiredRoles={['sysadmin']}>
          <LayoutWithBanner>
            <WBSSchemaDesignerPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      <Route path="/platform/users" element={
        <ProtectedRoute requiredRoles={['sysadmin']}>
          <LayoutWithBanner>
            <SystemUsersPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />
      <Route path="/platform/audit-log" element={
        <ProtectedRoute requiredRoles={['sysadmin']}>
          <LayoutWithBanner>
            <TenantDirectoryPage />
          </LayoutWithBanner>
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <TenantProvider>
          <ProductModeProvider>
            <HierarchyProvider>
              <PersonaProvider>
                <FilterProvider>
                  <SupportContextProvider>
                    <SyncNotificationProvider>
                      <OnboardingProvider>
                        <AppRoutes />
                        <SupportDrawer />
                        <SupportFAB />
                        <ReauthModal />
                        {/* TenantDebugIndicator removed for production - was debug tool */}
                        <Toaster position="top-right" richColors closeButton />
                      </OnboardingProvider>
                    </SyncNotificationProvider>
                  </SupportContextProvider>
                </FilterProvider>
              </PersonaProvider>
            </HierarchyProvider>
          </ProductModeProvider>
        </TenantProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
